/**
 * These two custom queries help populate and update a company’s profile page,
 * including the contacts tab.
 *
 * It is important to keep the fields in sync for both queries, because the
 * response from the mutation will re-populate the profile page. Thus, if a
 * field is added to the get Query, it should be added to the mutation query
 * as well.
 */

export const getCompanyForProfile = /* GraphQL */ `
  query GetCompanyForProfile($id: ID!) {
    getCompany(id: $id) {
      id
      name
      displayCompanyName
      lowerNameKey
      slug
      type
      logo {
        version
        original {
          bucket
          key
          level
          region
        }
        thumbnails {
          size
          image {
            bucket
            key
            level
            region
          }
        }
      }
      domainWhitelist
      isPartnerCompany
      onboardingSections {
        companyInfo
        contactInfo
        companyPreferences
        immigrationPreferences
        paymentPreferences
        questions
        employeeInformation
      }
      isParent
      parentID
      archived
      companyNumber
      createdAt
      updatedAt
      children(archivedLowerNameKey: { beginsWith: { archived: ACTIVE } }) {
        items {
          id
          name
          slug
        }
      }
      parent {
        id
        name
        slug
        domainWhitelist
        isPartnerCompany
        isParent
        parentID
        archived        
        createdAt
        updatedAt
      }
      jobDescriptions(
        limit: 500
        archivedType: { eq: { archived: ACTIVE, type: STANDARD } }
      ) {
        items {
          id
          title
        }
      }
      accountManagerID
      accountManager {
        avatar {
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          original {
            bucket
            identityId
            key
            level
            region
          }
        }
        firstname
        lastname
        id
        slug
        lowerFirst
        lowerLast
        lowerFull
        archived
        createdAt
        updatedAt
        type
      }
      assignedAttorneys(limit: 500) {
        items {
          id
          attorney {
            avatar {
              squareThumbnails {
                image {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                size
              }
              original {
                bucket
                identityId
                key
                level
                region
              }
            }
            firstname
            lastname
            id
            slug
          }
        }
      }
      assignedParalegals(limit: 500) {
        items {
          id
          paralegal {
            avatar {
              squareThumbnails {
                image {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                size
              }
              original {
                bucket
                identityId
                key
                level
                region
              }
            }
            firstname
            lastname
            id
            slug
          }
        }
      }
      assignedEmployerAdmins(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      invoicingContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      primaryContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      seniorContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      signatoryContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      assignedEmployers(limit: 500) {
        items {
          __typename
          id
          employer {
            archived
            id
            type
            email
            loginStatus {
              isEnabled
              status
            }
            firstname
            mostRecentlyInvited
            username
            lastname
            phone
            slug
            avatar {
              original {
                bucket
                region
                key
                level
                identityId
              }
              square {
                bucket
                region
                key
                level
                identityId
              }
              squareThumbnails {
                size
              }
              thumbnails {
                size
              }
              version
            }
          }
        }
      }
      fileRequirements {
        type
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
      }
      files {
        items {
          id
          expirationDate
          durationOfStatus
          dueDate
          issueDate
          fileName
          fileType
          contentType
          original {
            bucket
            region
            key
            level
            identityId
          }
          processRequestID
          companyID
          sourceID
          ownerNationalID
          uploadDate
          uploaderID
          uploaderEmployer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderForeignNational {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderTeamMember {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          createdAt
          updatedAt
        }
      }
      notes
      petitionerParagraph
      data {
        id
        companyID
        allowsEmployeesToTelecommute {
          value
          followPrompt
          followValue
          followPromptSecond
          followValueSecond
        }
        placesEmployeesAtClientSitesForMoreThan30DaysAtATime
        preferredMailingAddress
        companyNameShowToFN
        companyCoversExpenseOfDependentPetitionsAndOrEADCards
        companyCoversExpenseOfDependentPetitions
        doingBusinessAs
        educationEvaluation
        employeesEmailMHAttorneysAndParalegalsDirectly
        fein
        fullTimeEmployeesInUS
        annualMeetingDate,
        requiresMeetingReminder,
        isH1BDependentEmloyer
        hasBeenWillfulViolator
        isHigherEd_20_USC_1001_a
        isHigherEdNonProfit_8_CFR_214_2_h_19_iii_B
        isHigherEdNonProfit_8_CFR_214_2_h_8_ii_F_2
        isNonProfitResearch_8_CFR_214_2_h_19_iii_C
        isNonProfitResearch_8_CFR_214_2_h_8_ii_F_3
        isQualifyingCapExempt_8_CFR_214_2_h_8_ii_F_4
        maintainsAWrittenImmigrationPolicy
        naicsCodeOfCompany
        partTimeEmployeesInUS
        paymentPreference
        permissionPowerAttorney
        permissionReleaseApprovalNotices
        permissionReleasePetitionsRFE
        permissionReleaseLCA
        permissionRelease9089
        phone
        placesEmployeesAtClientSitesForMoreThanACoupleOfDaysAtATime
        fax
        preferredShippingSpeedForDocuments
        shipOriginalApprovalNotices
        staffsEmployeesAtThirdPartyEndClientSites
        typeOfBusiness
        usesWageSurvey
        usesEVerify
        utilizePremiumProcessingService
        technologyReleaseLicenseRequirement
        yearCompanyWasFoundedInUS
        I9_audit_has_been_completed_in_past_18_months
        current_number_of_H1B_employees
        current_number_of_employees_on_L_visas
        other_visa_statuses_sponsored
        email_address_invoices_should_be_addressed_to
        name_invoices_should_be_addressed_to
        createdAt
        updatedAt
        preferenceForEmployeesToPayPartOfGreenCard {
          value
          reason
        }
        preferenceForEmployeesToPayPartOfH1B {
          value
          reason
        }
        employs50OrMore {
          value
          followPrompt
          followValue
        }
        grossRevenueForMostRecentFiscalYear {
          amount
          currency
        }
        netRevenueForMostRecentFiscalYear {
          amount
          currency
        }
        usesApplicantTrackingSystem {
          reason
          value
        }
        usesHRSSystem {
          reason
          value
        }
        shippingPreference {
          fedexAccount
          preference
          upsAccount
        }
        addressSecondary {
          careOf
          address
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
          companyAddressType
        }
        addressPrimary {
          careOf
          address
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
          companyAddressType
        }
      }
    }
  }
`;

export const updateCompanyForProfile = /* GraphQL */ `
  mutation UpdateCompanyForProfile(
    $input: UpdateCompanyInput!
    $condition: ModelCompanyConditionInput
  ) {
    updateCompany(input: $input, condition: $condition) {
      id
      name
      displayCompanyName
      lowerNameKey
      slug
      type
      notes
      petitionerParagraph
      onboardingSections {
        companyInfo
        contactInfo
        companyPreferences
        immigrationPreferences
        paymentPreferences
        questions
        employeeInformation
      }
      completedOnboardingQuestionsBy
      logo {
        version
        original {
          bucket
          key
          level
          region
        }
        thumbnails {
          size
          image {
            bucket
            key
            level
            region
          }
        }
      }
      domainWhitelist
      isPartnerCompany
      isParent
      parentID
      archived
      companyNumber
      createdAt
      updatedAt
      children(archivedLowerNameKey: { beginsWith: { archived: ACTIVE } }) {
        items {
          id
          name
          slug
        }
      }
      parent {
        id
        name
        slug
        domainWhitelist
        isPartnerCompany
        isParent
        parentID
        archived
        createdAt
        updatedAt
      }
      jobDescriptions(
        limit: 500
        archivedType: { eq: { archived: ACTIVE, type: STANDARD } }
      ) {
        items {
          id
          title
        }
      }
      accountManagerID
      accountManager {
        avatar {
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          original {
            bucket
            identityId
            key
            level
            region
          }
        }
        firstname
        lastname
        id
        slug
        lowerFirst
        lowerLast
        lowerFull
        archived
        createdAt
        updatedAt
        type
      }
      assignedAttorneys(limit: 500) {
        items {
          id
          attorney {
            avatar {
              squareThumbnails {
                image {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                size
              }
              original {
                bucket
                identityId
                key
                level
                region
              }
            }
            firstname
            lastname
            id
            slug
          }
        }
      }
      assignedParalegals(limit: 500) {
        items {
          id
          paralegal {
            avatar {
              squareThumbnails {
                image {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                size
              }
              original {
                bucket
                identityId
                key
                level
                region
              }
            }
            firstname
            lastname
            id
            slug
          }
        }
      }
      assignedEmployerAdmins(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      invoicingContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      primaryContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      seniorContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      signatoryContacts(limit: 500) {
        items {
          __typename
          id
          employerID
        }
      }
      assignedEmployers(limit: 500) {
        items {
          __typename
          id
          employer {
            archived
            id
            type
            email
            loginStatus {
              isEnabled
              status
            }
            firstname
            mostRecentlyInvited
            username
            lastname
            phone
            slug
            avatar {
              original {
                bucket
                region
                key
                level
                identityId
              }
              square {
                bucket
                region
                key
                level
                identityId
              }
              squareThumbnails {
                size
              }
              thumbnails {
                size
              }
              version
            }
          }
        }
      }
      fileRequirements {
        type
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
      }
      files {
        items {
          id
          expirationDate
          durationOfStatus
          fileName
          fileType
          contentType
          original {
            bucket
            region
            key
            level
            identityId
          }
          processRequestID
          companyID
          sourceID
          ownerNationalID
          uploadDate
          uploaderID
          uploaderEmployer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderForeignNational {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderTeamMember {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          createdAt
          updatedAt
        }
      }
      data {
        id
        companyID
        allowsEmployeesToTelecommute {
          value
          followPrompt
          followValue
          followPromptSecond
          followValueSecond
        }
        placesEmployeesAtClientSitesForMoreThan30DaysAtATime
        preferredMailingAddress
        companyNameShowToFN
        annualMeetingDate,
        requiresMeetingReminder,
        companyCoversExpenseOfDependentPetitionsAndOrEADCards
        companyCoversExpenseOfDependentPetitions
        doingBusinessAs
        educationEvaluation
        employeesEmailMHAttorneysAndParalegalsDirectly
        fein
        fullTimeEmployeesInUS
        isH1BDependentEmloyer
        hasBeenWillfulViolator
        isHigherEd_20_USC_1001_a
        isHigherEdNonProfit_8_CFR_214_2_h_19_iii_B
        isHigherEdNonProfit_8_CFR_214_2_h_8_ii_F_2
        isNonProfitResearch_8_CFR_214_2_h_19_iii_C
        isNonProfitResearch_8_CFR_214_2_h_8_ii_F_3
        isQualifyingCapExempt_8_CFR_214_2_h_8_ii_F_4
        maintainsAWrittenImmigrationPolicy
        naicsCodeOfCompany
        partTimeEmployeesInUS
        paymentPreference
        permissionPowerAttorney
        permissionReleaseApprovalNotices
        permissionReleasePetitionsRFE
        permissionReleaseLCA
        permissionRelease9089
        phone
        placesEmployeesAtClientSitesForMoreThanACoupleOfDaysAtATime
        fax
        preferredShippingSpeedForDocuments
        shipOriginalApprovalNotices
        staffsEmployeesAtThirdPartyEndClientSites
        typeOfBusiness
        usesWageSurvey
        usesEVerify
        utilizePremiumProcessingService
        technologyReleaseLicenseRequirement
        yearCompanyWasFoundedInUS
        I9_audit_has_been_completed_in_past_18_months
        current_number_of_H1B_employees
        current_number_of_employees_on_L_visas
        other_visa_statuses_sponsored
        email_address_invoices_should_be_addressed_to
        name_invoices_should_be_addressed_to
        createdAt
        updatedAt
        preferenceForEmployeesToPayPartOfGreenCard {
          value
          reason
        }
        preferenceForEmployeesToPayPartOfH1B {
          value
          reason
        }
        employs50OrMore {
          value
          followPrompt
          followValue
        }
        grossRevenueForMostRecentFiscalYear {
          amount
          currency
        }
        netRevenueForMostRecentFiscalYear {
          amount
          currency
        }
        usesApplicantTrackingSystem {
          reason
          value
        }
        usesHRSSystem {
          reason
          value
        }
        shippingPreference {
          fedexAccount
          preference
          upsAccount
        }
        addressSecondary {
          careOf
          address
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
          companyAddressType
        }
        addressPrimary {
          careOf
          address
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
          companyAddressType
        }
      }
    }
  }
`;

/* Keep the fields in getFirm and updateFirm in sync */
export const getFirm = /* GraphQL */ `
 query GetFirmForProfile($id: ID!) {
   getFirm(id: $id) {
     id
     slug
     name
     logo {
       version
       original {
         bucket
         key
         level
         region
       }
       thumbnails {
         size
         image {
           bucket
           key
           level
           region
         }
       }
     }
     domainWhitelist
     address {
       careOf
       address
       unitNumber
       unitType
       adminArea
       state
       city
       country
       postalCode
       title
     }
     mailingAddress, {
       careOf
       address
       unitNumber
       unitType
       adminArea
       state
       city
       country
       postalCode
       title
     }
     doingBusinessAs
     phone
     fax
   }
 }
`;

export const updateFirm = /* GraphQL */ `
 mutation UpdateFirmForProfile(
   $input: UpdateFirmInput!
   $condition: ModelFirmConditionInput
 ) {
   updateFirm(input: $input, condition: $condition) {
     id
     name
     logo {
       version
       original {
         bucket
         key
         level
         region
       }
       thumbnails {
         size
         image {
           bucket
           key
           level
           region
         }
       }
     }
     domainWhitelist
     address {
       careOf
       address
       unitNumber
       unitType
       adminArea
       state
       city
       country
       postalCode
       title
     }
     mailingAddress {
       careOf
       address
       unitNumber
       unitType
       adminArea
       state
       city
       country
       postalCode
       title
     }
     doingBusinessAs
     phone
     fax
   }
 }
`;

export const searchForeignNationals = /* GraphQL */ `
 query CompanySearchForeignNationals(
   $filter: SearchableForeignNationalFilterInput
   $sort: [SearchableForeignNationalSortInput]
   $limit: Int
   $nextToken: String
   $from: Int
 ) {
   searchForeignNationals(
     filter: $filter
     sort: $sort
     limit: $limit
     nextToken: $nextToken
     from: $from
   ) {
     items {
       type
       id
       slug
       firstname
       lastname
       employeeId
       birthCountry
       citizenshipCountry
       jobDescriptionID
       offeredSalary {
         currency
         amount
         period
       }
       files {
         items {
           fileType
         }
       }
       avatar {
         squareThumbnails {
           image {
             bucket
             identityId
             key
             level
             region
           }
           size
         }
         original {
           bucket
           identityId
           key
           level
           region
         }
       }
       workLocation {
         careOf
         address
         companyAddressType
         unitNumber
         unitType
         adminArea
         state
         city
         country
         postalCode
         title
       }
     }
     nextToken
     total
   }
 }
`;
